import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { NomenclaturaActions } from 'Store/Nomenclatura/Nomenclatura.actions';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { useQueryUpdateInfoFiles } from 'Hooks/files/useQueryUpdateInfoFiles';
import {
  INomenclatureData,
  VersionFieldEnumLabel,
  SeparatorsData,
} from 'Data/interfaces/Nomenclatura/INomenclatureData';
import { IUploadFileData } from 'Data/interfaces/Upload/IUploadFileData';
import schema, { createSchema } from './UploadFiles.schema';
import ListContainer from '../Containers/ListContainer/ListContainer';
import { Icon } from '../Icon';
import Field from '../Field';
import Form from '../Form/Form';
import TextArea from '../Textarea';
import RadioButtons from '../RadioButtons';
import { sizeFileRangeToInitials } from 'Utils/toolBox';
import { iconFileByFilename } from 'Utils/Icons';
import {
  ConfirmButton,
  FileItemWrapper,
  FilenameAndEditWrapper,
  FormWrapper,
  NewFileNameOptionsWrap,
  NomenclatureFieldsWrapper,
  OpenOriginFolder,
  RemoveFileButton,
  RetryButton,
} from './styles';
import 'react-circular-progressbar/dist/styles.css';

export interface IFileItem {
  index: number;
  uploadFile: IUploadFileData;
  nomenclature: INomenclatureData;
  isLoading: boolean;
  testShowEdit?: boolean;
  removeFile: (index: number) => void;
  changeFilename: (index: number, name: string) => void;
  retryFile: (index: number) => void;
}

const FileItem: FC<IFileItem> = (props) => {
  const { t } = useTranslation('listagemArquivos');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateInfoFiles } = useQueryUpdateInfoFiles();

  const theme = useSelector(getTheme);
  const [isShowEdit, setIsShowEdit] = useState(
    props.testShowEdit ? props.testShowEdit : false);
  const [newNameFileExistSelected, setNewNameFileExistSelected] = useState<string | number | undefined>();

  const showEdit = () => setIsShowEdit(true);

  const checkUpload = props.uploadFile?.checkUploadFileInfo;
  // Lê a marca gravada no arquivo, não o cache: o estado da tela não pode mudar
  // sozinho quando a renderização da versão anterior terminar.
  const previousVersionRendering = !!props.uploadFile?.renderingBlocked;

  useEffect(() => {
    if (checkUpload) {
      // Com a versão anterior renderizando, "Nova revisão" está bloqueada — a
      // seleção inicial vai para a cópia, que é um arquivo novo e independente.
      setNewNameFileExistSelected(previousVersionRendering
        ? checkUpload?.FileNameCopy
        : checkUpload?.FileNameNewRevision);
    }
  }, [checkUpload, previousVersionRendering]);

  if (!props.nomenclature.VersionFields) {
    dispatch(NomenclaturaActions.getNomenclature());
  }

  const hasNomenclatureError = () => {
    return checkUpload && checkUpload.HasInconsistencia;
  };

  const getEditForms = () => {
    if (isShowEdit && hasNomenclatureError() && checkUpload && checkUpload.HasInconsistencia) {
      return nomenclatureForm;
    }

    if (checkUpload && checkUpload.FileNameExist) {
      return existFileForm;
    }

    // Sem o form de nome existente o arquivo ficaria retido em silêncio: o aviso
    // é o que explica por que o envio não anda.
    if (previousVersionRendering) {
      return renderingBlockedWarning;
    }

    if (props.uploadFile.updateFileInfo && !props.uploadFile.updateFileInfo.finished) {
      return descriptionForm;
    }

    return '';
  };

  const renderingWarning = (
    <div className="renderingWarningWrap">
      <span className="renderingWarning">
        {t('upload.previousVersionRendering')}
      </span>
      <RetryButton onClick={() => props.retryFile(props.index)}>
        {t('upload.retryPreviousVersionRendering')}
      </RetryButton>
    </div>
  );

  const renderingBlockedWarning = (
    <NewFileNameOptionsWrap>
      {renderingWarning}
    </NewFileNameOptionsWrap>
  );

  const nomenclatureSubmit = (data: any) => {
    const fields = Object.keys(data);
    let filename = '';
    for (let i = 0; i < fields.length / 2; i++) {
      filename += `${data[`field${i}`]}${data[`separator${i}`]}`;
    }
    const splittedValues = props.uploadFile.file.name.split('.');
    const extension = splittedValues[splittedValues.length - 1];
    filename = `${filename.slice(0, -1)}.${extension}`;

    props.changeFilename(props.index, filename);
  };

  const getWidthInput = (label: string) => {
    const width = label.length * 8.5;
    return width < 90 ? undefined : { minWidth: width } as CSSProperties;
  }

  const existFileForm = (
    <NewFileNameOptionsWrap>
      <div className="titleButtonWrap">
        <span className="existFileTitle">
          Nome do arquivo já existe, selecione uma opção:
        </span>
        <ConfirmButton
          type='submit'
          isnewfilename="true"
          onClick={() => props.changeFilename(props.index, String(newNameFileExistSelected))}
        >
          <Icon icon='confirmacao' customSize={26} className="confirmIcon" />
        </ConfirmButton>
      </div>
      {previousVersionRendering && renderingWarning}
      <div className="radioButtonsWrap">
        <RadioButtons
          options={[
            {
              value: checkUpload?.FileNameNewRevision || '',
              disabled: previousVersionRendering,
              label: (
                <div className="newFileNameOption">
                  <div>{checkUpload?.FileNameNewRevision}</div>
                  <div className="titleOption">Nova revisão</div>
                </div>
              ),
            },
            {
              value: checkUpload?.FileNameCopy || '',
              label: (
                <div className="newFileNameOption">
                  <div>{checkUpload?.FileNameCopy}</div>
                  <div className="titleOption">Cópia</div>
                </div>
              ),
            },
          ]}
          onSelect={setNewNameFileExistSelected}
          defaultValue={previousVersionRendering
            ? checkUpload?.FileNameCopy
            : checkUpload?.FileNameNewRevision} />
      </div>
    </NewFileNameOptionsWrap>
  );

  const nomenclatureForm = (
    <Form onSubmit={nomenclatureSubmit} schema={createSchema(props.nomenclature.VersionFields)}>
      {(propsField: any) => (
        <FormWrapper>
          <NomenclatureFieldsWrapper isstory={props.testShowEdit}>
            {props.nomenclature.VersionFields.map((field, index) => (
              <span
                key={`fields${index}`}
                style={getWidthInput(field.CampoPersonalizado || VersionFieldEnumLabel.get(field.CampoVersaoId) || '')}
                className="fieldsWrapper"
              >
                <Field
                  key={`field${index}`}
                  name={`field${field.Ordem}`}
                  defaultValue={checkUpload?.Division[index]}
                  label={field.CampoPersonalizado
                    || VersionFieldEnumLabel.get(field.CampoVersaoId)
                    || ''}
                  style={getWidthInput(field.CampoPersonalizado || VersionFieldEnumLabel.get(field.CampoVersaoId) || '')}
                  className="field"
                  height="30px"
                />
                <Field
                  key={`separator${index}`}
                  name={`separator${field.Ordem}`}
                  label=''
                  value={SeparatorsData.get(field.Separador)?.Value}
                  type='hidden'
                  height="30px"
                />
              </span>
            ))}
          </NomenclatureFieldsWrapper>
          <ConfirmButton
            type='submit'
            disabled={propsField.hasValidationErrors}
          >
            <Icon icon='confirmacao' customSize={26} className="confirmIcon" />
          </ConfirmButton>
        </FormWrapper>
      )}
    </Form>
  );

  const descriptionSubmit = (data: any) => {
    if (props.uploadFile.updateFileInfo) {
      if (!data.description) {
        props.uploadFile.updateFileInfo.notShowToast = true;
      }
      props.uploadFile.updateFileInfo.Description = data.description;
      updateInfoFiles({
        constructionSiteId: props.uploadFile.csId,
        folderId: props.uploadFile.folderId,
        files: [props.uploadFile.updateFileInfo]
      });
    };
  }

  const descriptionForm = (
    <Form
      initialValues={{ description: props.uploadFile.updateFileInfo?.Description }}
      onSubmit={descriptionSubmit}
      schema={schema}
    >
      <FormWrapper isstory={props.testShowEdit} isdescription>
        <TextArea
          name='description'
          label={t('upload.fileDescriptionLabel')}
          placeholder={t('upload.fileDescriptionPlaceholder')}
          style={{ height: 91 }}
        />
        <ConfirmButton type='submit' isdescription="true">
          <Icon icon='confirmacao' customSize={26} className="confirmIcon" />
        </ConfirmButton>
      </FormWrapper>
    </Form>
  );

  const handleOpenFolder = (uploadFile: IUploadFileData) => {
    if (!uploadFile.uploadBreadcrumbFile) return;

    navigate(`/obras/filelist/${uploadFile.csId}/${uploadFile.uploadBreadcrumbFile.fileParentId}${uploadFile.uploadBreadcrumbFile?.fileParentBreadcrumbId ? `?breadCrumbId=${uploadFile.uploadBreadcrumbFile.fileParentBreadcrumbId}` : ''}`);
  };

  if (props.uploadFile.removed) {
    return <></>;
  }

  return (
    <ListContainer height={'auto'}>
      <FileItemWrapper>
        <div className="fileItemDescriptionWrapper">
          <Icon
            icon={iconFileByFilename(props.uploadFile.file.name)}
            color={hasNomenclatureError() ? 'vermelho' : 'cinzaPadrao'}
            customSize={24} />
          <FilenameAndEditWrapper showedit={`${props.uploadFile.updateFileInfo && !props.uploadFile.updateFileInfo.finished}`}>
            <div className="filenameWrapper">
              <div className="fileName">
                {hasNomenclatureError() || props.uploadFile.uploadError ? (
                  <>
                    <span className="hasError">
                      {props.uploadFile.newFileName || props.uploadFile.file.name}
                    </span>
                    {hasNomenclatureError() ? (
                      <span className="hasError">Nomeclatura inválida</span>
                    ) : (
                      <span className="hasError">Erro ao enviar o arquivo</span>
                    )}
                  </>
                ) : (
                  <span>
                    {props.uploadFile.newFileName || props.uploadFile.file.name}
                  </span>
                )}

              </div>
              {props.uploadFile.isSending ? (
                <span className="fileSize">
                  {props.uploadFile.isProcessing
                    ? t('upload.processing')
                    : sizeFileRangeToInitials(props.uploadFile.sentSize, props.uploadFile.totalSize)}
                </span>
              ) : ''}
            </div>
            {hasNomenclatureError() && !isShowEdit && (
              <div className="editButton" onClick={showEdit}>
                <Icon icon='editar' color='primary' />
              </div>
            )}
          </FilenameAndEditWrapper>
          <div className="fileItemFooterWrapper">
            {((hasNomenclatureError() && isShowEdit) || previousVersionRendering) && (
              <RemoveFileButton onClick={() => props.removeFile(props.index)}>
                <Icon icon='cancelar' color='cinzaPadrao' customSize={10} />
              </RemoveFileButton>
            )}
            {props.uploadFile.isSending && (!props.uploadFile.updateFileInfo?.finished || props.uploadFile.updateFileInfo?.finished === undefined) ? (
              <CircularProgressbar
                className="loading"
                strokeWidth={14}
                styles={buildStyles({
                  pathColor: theme.colors.primary.primary,
                  trailColor: theme.colors.secondary.secondary
                })}
                value={props.uploadFile.sentSize / props.uploadFile.totalSize}
                maxValue={1}
              />
            ) : <></>}
            {props.uploadFile.updateFileInfo?.finished ? (
              <OpenOriginFolder
                onClick={() => handleOpenFolder(props.uploadFile)}
              >
                <Icon icon='arquivar' customSize={11} className="openCloudServiceIcon" />
              </OpenOriginFolder>
            ) : ''}
            {!(props.uploadFile.isSending || props.uploadFile.updateFileInfo?.finished || props.uploadFile.uploadCompleted || hasNomenclatureError() || checkUpload?.FileNameExist || previousVersionRendering || props.uploadFile.uploadError) ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 20 }}
                    color={theme.colors.primary.primary}
                    spin
                  />
                }
              />
            ) : ''}
          </div>
        </div>

        {getEditForms()}
      </FileItemWrapper>
    </ListContainer>
  );
};

export default FileItem;
